import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  createTheme,
  ThemeProvider,
  Avatar,
  Grid,
  Card,
  CardContent,
  Autocomplete,
} from "@mui/material";
import MUIDataTable from "mui-datatables";
import { LocalizationProvider } from "@mui/x-date-pickers";
import DatePicker from "../../../Components/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { DriversSalary, clientApis } from "../../../Data/axios";
import config from "../../../Data/AuthUser";
import StatusAlert from "../../../StatusAlertList/StatusAlert";
import { useNavigate, useLocation } from "react-router-dom";

// Driver Avatar Component
const DriverAvatar = ({ username, userId, userImage }) => {
  const [imageError, setImageError] = useState(false);

  const getInitials = (name) => {
    if (!name) return "NA";
    const words = name.trim().split(" ");
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    }
    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  };

  const imageUrl = userImage && userImage !== 'non' && !imageError
    ? `${config.apiImageUrl}${userImage}`
    : null;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
      {imageUrl ? (
        <Avatar
          src={imageUrl}
          alt={username}
          onError={() => setImageError(true)}
          sx={{
            width: 30,
            height: 30,
            backgroundColor: "#FBC02D",
            color: "black",
            fontWeight: "bold",
            fontSize: "12px"
          }}
        >
          {getInitials(username)}
        </Avatar>
      ) : (
        <Avatar
          sx={{
            width: 30,
            height: 30,
            backgroundColor: "#FBC02D",
            color: "black",
            fontWeight: "bold",
            fontSize: "12px"
          }}
        >
          {getInitials(username)}
        </Avatar>
      )}
      <div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <Typography variant="body2" fontWeight="bold" color="black" sx={{ lineHeight: '1.2', marginBottom: '2px' }}>
          {username}
        </Typography>
        <Typography variant="caption" color="gray" sx={{ lineHeight: '1.2' }}>
          {userId}
        </Typography>
      </div>
    </div>
  );
};

// Client Avatar Component
const ClientAvatar = ({ clientName, clientId, clientImage }) => {
  const [imageError, setImageError] = useState(false);

  const getInitials = (name) => {
    if (!name) return "NA";
    const words = name.trim().split(" ");
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    }
    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  };

  const imageUrl = clientImage && clientImage !== 'non' && !imageError
    ? `${config.apiImageUrl}${clientImage}`
    : null;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
      {imageUrl ? (
        <Avatar
          src={imageUrl}
          alt={clientName}
          onError={() => setImageError(true)}
          sx={{
            width: 30,
            height: 30,
            backgroundColor: "#2196f3",
            color: "white",
            fontWeight: "bold",
            fontSize: "12px"
          }}
        >
          {getInitials(clientName)}
        </Avatar>
      ) : (
        <Avatar
          sx={{
            width: 30,
            height: 30,
            backgroundColor: "#2196f3",
            color: "white",
            fontWeight: "bold",
            fontSize: "12px"
          }}
        >
          {getInitials(clientName)}
        </Avatar>
      )}
      <div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <Typography variant="body2" fontWeight="bold" color="black" sx={{ lineHeight: '1.2', marginBottom: '2px' }}>
          {clientName}
        </Typography>
        <Typography variant="caption" color="gray" sx={{ lineHeight: '1.2' }}>
          {clientId}
        </Typography>
      </div>
    </div>
  );
};

const DriverSalaryTransactions = ({ clientId: propClientId, fromDate: propFromDate, toDate: propToDate }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 5, // Match backend default
    count: 0,
    totalPages: 0,
  });

  // State for Autocomplete dropdowns
  const [clients, setClients] = useState([]);
  const [clientSearchQuery, setClientSearchQuery] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);

  // State for Filters
  const [filters, setFilters] = useState({
    name: "",
    phone: "",
    user_id: "",
    client_id: propClientId || "",
    status: "",
    timestamps: "",
  });

  // State for Date Range (Calendar) - initialize with props if provided
  const [fromDate, setFromDate] = useState(propFromDate ? dayjs(propFromDate) : null);
  const [toDate, setToDate] = useState(propToDate ? dayjs(propToDate) : null);

  // State for Update Dialog
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [updateStatus, setUpdateStatus] = useState("");
  const [updateTransferId, setUpdateTransferId] = useState("");

  // State for Alerts
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("info");

  const showAlert = (message, severity) => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  // Fetch Clients for Dropdown
  const fetchClients = async (query = "") => {
    try {
      let response = await clientApis.getAll(1, 50, query ? "company_name" : undefined, query);
      if (response && response.data) {
        if (response.data.length === 0 && query) {
          response = await clientApis.getAll(1, 50, "username", query);
        }
        if (response && response.data) {
          setClients(response.data);
        }
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  // Debounced search for clients
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (clientSearchQuery) {
        fetchClients(clientSearchQuery);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [clientSearchQuery]);

  // Initial load of clients
  useEffect(() => {
    fetchClients();
  }, []);

  // Fetch transactions
  const fetchTransactions = async (page = 0, rowsPerPage = 10) => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };

      // Add filters if they have values
      if (filters.name) params.name = filters.name;
      if (filters.phone) params.phone = filters.phone;
      if (filters.user_id) params.user_id = filters.user_id;
      if (filters.client_id) params.client_id = filters.client_id;
      if (filters.status !== "") params.status = filters.status;
      if (filters.timestamps) params.timestamps = filters.timestamps;

      const response = await DriversSalary.getDriverSalary(params);

      if (response.success) {
        setTransactions(response.data || []);
        setPagination((prev) => ({
          ...prev,
          count: response.pagination?.total_records || 0,
          totalPages: response.pagination?.total_pages || 0,
        }));
      } else {
        showAlert(response.message || "Failed to fetch transactions", "error");
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
      showAlert("An error occurred while fetching transactions", "error");
    } finally {
      setLoading(false);
    }
  };

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.client_id) params.append('client_id', filters.client_id);
    if (fromDate) params.append('from_date', dayjs(fromDate).format('YYYY-MM-DD'));
    if (toDate) params.append('to_date', dayjs(toDate).format('YYYY-MM-DD'));

    const queryString = params.toString();
    navigate(`${location.pathname}${queryString ? '?' + queryString : ''}`, { replace: true });
  }, [filters.client_id, fromDate, toDate]);

  // Initialize with passed props and fetch data
  useEffect(() => {
    const initializeFilters = async () => {
      if (propClientId) {
        setFilters(prev => ({
          ...prev,
          client_id: propClientId
        }));

        // Fetch client details if not already in the list
        try {
          const response = await clientApis.getAll(1, 1, "user_id", propClientId);
          if (response && response.data && response.data.length > 0) {
            setSelectedClient(response.data[0]);
            if (clients.length === 0) {
              setClients(response.data);
            }
          } else {
            // If no specific client found, at least set the basic object
            setSelectedClient({ user_id: propClientId, company_name: propClientId });
          }
        } catch (error) {
          console.error("Error fetching client details:", error);
          setSelectedClient({ user_id: propClientId, company_name: propClientId });
        }
      }

      if (propFromDate && propToDate) {
        const from = dayjs(propFromDate).format("YYYY-MM-DD");
        const to = dayjs(propToDate).format("YYYY-MM-DD");
        setFilters(prev => ({
          ...prev,
          timestamps: `${from},${to}`
        }));
      }

      fetchTransactions(0, 5); // Match backend defaults
    };

    initializeFilters();
  }, [propClientId, propFromDate, propToDate]);

  // Handle Update Button Click
  const handleUpdateClick = (transaction) => {
    setSelectedTransaction(transaction);
    setUpdateStatus(transaction.status.toString());
    setUpdateTransferId(transaction.transfer_id === "N/A" ? "" : transaction.transfer_id);
    setUpdateDialogOpen(true);
  };

  // Handle Update Submit
  const handleUpdateSubmit = async () => {
    if (!selectedTransaction) return;

    setLoading(true);
    try {
      const payload = {
        status: parseInt(updateStatus),
        transfer_id: updateTransferId || undefined,
      };

      const response = await DriversSalary.updateDriverSalary(
        selectedTransaction.id,
        payload
      );

      if (response.success) {
        showAlert("Transaction updated successfully", "success");
        setUpdateDialogOpen(false);
        // Refresh the table
        fetchTransactions(pagination.page, pagination.rowsPerPage);
      } else {
        showAlert(response.message || "Failed to update transaction", "error");
      }
    } catch (error) {
      console.error("Error updating transaction:", error);
      showAlert("An error occurred while updating transaction", "error");
    } finally {
      setLoading(false);
    }
  };

  // Status label helper
  const getStatusLabel = (status) => {
    switch (status.toString()) {
      case "0":
        return "Initiated";
      case "1":
        return "Processed";
      case "2":
        return "Transferred";
      default:
        return "Unknown";
    }
  };

  // Status color helper
  const getStatusColor = (status) => {
    switch (status.toString()) {
      case "0":
        return "#ff9800"; // Orange
      case "1":
        return "#2196f3"; // Blue
      case "2":
        return "#4caf50"; // Green
      default:
        return "#757575"; // Grey
    }
  };

  // Theme for white background table
  const getWhiteTheme = () =>
    createTheme({
      components: {
        MuiTableCell: {
          styleOverrides: {
            head: {
              backgroundColor: "#FBC02D",
              color: "black",
              fontWeight: "bold",
              textAlign: "center",
              verticalAlign: "middle",
              padding: "12px 16px",
              whiteSpace: "nowrap"
            },
            body: {
              textAlign: "left",
              color: "black",
              backgroundColor: "white",
              verticalAlign: "middle",
              padding: "12px 16px",
              wordBreak: "break-word"
            },
            root: {
              backgroundColor: "white",
            }
          }
        },
        MUIDataTableHeadCell: {
          styleOverrides: {
            root: {
              backgroundColor: "#FBC02D",
              color: "black",
              padding: "12px 16px",
            },
            sortAction: {
              '& path': {
                color: "black"
              }
            }
          }
        },
        MuiTableBody: {
          styleOverrides: {
            root: {
              backgroundColor: "white",
            }
          }
        },
        MuiTableRow: {
          styleOverrides: {
            root: {
              backgroundColor: "white",
              '&:hover': {
                backgroundColor: "#f5f5f5",
              }
            }
          }
        },
        MuiToolbar: {
          styleOverrides: {
            root: {
              minHeight: "48px",
              paddingLeft: "16px",
              paddingRight: "16px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            },
            regular: {
              minHeight: "48px",
            },
            gutters: {
              paddingLeft: "16px",
              paddingRight: "16px",
            }
          }
        },
        MuiSelect: {
          styleOverrides: {
            root: {
              width: "auto", // Make the select dropdown auto width instead of full width
              minWidth: "75px", // Set a minimum width
              maxWidth: "120px", // Set a maximum width to prevent it from being too wide
            },
            select: {
              width: "auto !important", // Force the select element to auto width
              minWidth: "75px !important",
              maxWidth: "120px !important",
            }
          }
        },
        MuiMenuItem: {
          styleOverrides: {
            root: {
              width: "auto", // Ensure menu items also follow the width constraints
              minWidth: "75px",
              maxWidth: "120px",
            }
          }
        },
        MuiMenu: {
          styleOverrides: {
            paper: {
              minWidth: "80px", // Set minimum width for menu popup
              maxWidth: "150px", // Set maximum width for menu popup
              width: "auto", // Auto width based on content
            }
          }
        },
        MuiAutocomplete: {
          styleOverrides: {
            popper: {
              width: "auto !important", // Make autocomplete dropdown auto width
              minWidth: "200px !important", // Minimum width for autocomplete
              maxWidth: "300px !important", // Maximum width for autocomplete
            },
            paper: {
              width: "auto !important",
              minWidth: "200px !important",
              maxWidth: "300px !important",
            }
          }
        },
        MuiFormControl: {
          styleOverrides: {
            root: {
              width: "auto", // Make form controls auto width
              minWidth: "80px", // Minimum width for form controls
              maxWidth: "150px", // Maximum width for form controls in tables
            }
          }
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              elevation: 0,
              width: "100%"
            }
          }
        },
        MuiTableContainer: {
          styleOverrides: {
            root: {
              width: "100%",
              overflowX: "auto",
              "@media (max-width: 600px)": {
                overflowX: "scroll"
              }
            }
          }
        }
      },
      overrides: {
        MUIDataTable: {
          paper: {
            width: "100%",
            "@media (max-width: 600px)": {
              width: "100%",
              overflowX: "scroll"
            }
          }
        },
        MUIDataTableBodyCell: {
          root: {
            "@media (max-width: 600px)": {
              fontSize: "0.8rem"
            }
          }
        }
      }
    });

  const columns = [
    {
      name: "driver",
      label: "Driver",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = transactions[tableMeta.rowIndex];
          if (!row) return value;

          return (
            <DriverAvatar
              username={row.user_username}
              userId={row.user_id}
              userImage={row.user_image}
            />
          );
        },
        setCellProps: () => ({
          style: {
            minWidth: "200px",
            maxWidth: "300px"
          }
        }),
        sort: false,
        filter: false
      }
    },
    {
      name: "client",
      label: "Client",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = transactions[tableMeta.rowIndex];
          if (!row) return value;

          return (
            <ClientAvatar
              clientName={row.company_name || row.client_username}
              clientId={row.client_id}
              clientImage={row.client_image}
            />
          );
        },
        setCellProps: () => ({
          style: {
            minWidth: "200px",
            maxWidth: "300px"
          }
        }),
        sort: false,
        filter: false
      }
    },
    {
      name: "salary",
      label: "Gross",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = transactions[tableMeta.rowIndex];
          return (
            <Typography variant="body2" fontWeight="bold" color="black" sx={{ textAlign: 'left' }}>
              ₹{parseFloat(row.salary).toFixed(2)}
            </Typography>
          );
        },
        setCellProps: () => ({
          style: {
            minWidth: "100px",
            width: "120px"
          }
        })
      }
    },
    {
      name: "status",
      label: "Status",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = transactions[tableMeta.rowIndex];
          const partiallyTransferred = parseFloat(row.deposit_amount) > 0 && String(row.status) === "2";
          const status = partiallyTransferred ? "Partially Transferred" : getStatusLabel(row.status);
          const color = partiallyTransferred ? "#E65100" : getStatusColor(row.status);

          return (
            <Typography
              variant="body2"
              fontWeight="bold"
              sx={{ color: color, textAlign: 'left' }}
            >
              {status}
            </Typography>
          );
        },
        setCellProps: () => ({
          style: {
            minWidth: "100px",
            width: "120px"
          }
        })
      }
    },
    {
      name: "note",
      label: "Note",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = transactions[tableMeta.rowIndex];
          return (
            <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'left' }}>
              {row.note || "N/A"}
            </Typography>
          );
        },
        setCellProps: () => ({
          style: {
            minWidth: "150px",
            maxWidth: "250px"
          }
        })
      }
    },
    {
      name: "net_salary",
      label: "Net Salary",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = transactions[tableMeta.rowIndex];
          // Parse claim from note field: "Claim: 500, Remarks: Extra work"
          let claimAmount = 0;
          if (row.note && row.note.includes('Claim:')) {
            const claimMatch = row.note.match(/Claim:\s*([\d.]+)/i);
            if (claimMatch) {
              claimAmount = parseFloat(claimMatch[1]) || 0;
            }
          }

          let advanceAmount = 0;
          if (row.note && row.note.includes('Advance:')) {
            const advanceMatch = row.note.match(/Advance:\s*([\d.]+)/i);
            if (advanceMatch) {
              advanceAmount = parseFloat(advanceMatch[1]) || 0;
            }
          }

          // Deposit hold is deducted from the net payable as well
          const depositAmount = parseFloat(row.deposit_amount) || 0;

          const netSalary = parseFloat(row.salary) - claimAmount - advanceAmount - depositAmount;

          return (
            <Typography variant="body2" fontWeight="bold" color="green" sx={{ textAlign: 'left' }}>
              ₹{netSalary.toFixed(2)}
            </Typography>
          );
        },
        setCellProps: () => ({
          style: {
            minWidth: "120px",
            width: "140px"
          }
        })
      }
    },
    {
      name: "actions",
      label: "Actions",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = transactions[tableMeta.rowIndex];
          return (
            <Button
              variant="contained"
              size="small"
              onClick={() => handleUpdateClick(row)}
              sx={{
                backgroundColor: "#9c27b0",
                "&:hover": { backgroundColor: "#7b1fa2" },
                textTransform: "none",
              }}
            >
              Update
            </Button>
          );
        },
        setCellProps: () => ({
          style: {
            minWidth: "100px",
            width: "120px",
            textAlign: 'center'
          }
        })
      }
    },
  ];

  const options = {
    selectableRows: "none",
    filter: false,
    viewColumns: false,
    search: false,
    download: false,
    print: false,
    pagination: true,
    elevation: 0,
    responsive: "scrollMaxHeight",
    scrollMaxHeight: "calc(100vh - 300px)",
    count: pagination.count,
    page: pagination.page,
    rowsPerPage: pagination.rowsPerPage,
    rowsPerPageOptions: [5, 10, 25, 50, 100],
    setTableProps: () => ({
      style: {
        display: 'table',
        width: '100%',
        tableLayout: 'auto',
      },
    }),
    onChangePage: (page) => {
      setPagination((prev) => ({ ...prev, page }));
      fetchTransactions(page, pagination.rowsPerPage);
    },
    onChangeRowsPerPage: (rowsPerPage) => {
      setPagination((prev) => ({ ...prev, rowsPerPage, page: 0 }));
      fetchTransactions(0, rowsPerPage);
    },
    textLabels: {
      body: {
        noMatch: loading ? "Loading..." : "No transactions found",
      },
      pagination: {
        next: "Next",
        previous: "Previous",
        rowsPerPage: "Rows per page:",
        displayRows: "of",
      },
    },
  };

  // Handle Filter Change
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle Date Picker Changes -> builds the timestamps string the backend expects
  const handleFromDateChange = (date) => {
    setFromDate(date);
    const from = date ? dayjs(date).format("YYYY-MM-DD") : "";
    const to = toDate ? dayjs(toDate).format("YYYY-MM-DD") : "";
    handleFilterChange("timestamps", from && to ? `${from},${to}` : from || to);
  };

  const handleToDateChange = (date) => {
    setToDate(date);
    const from = fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : "";
    const to = date ? dayjs(date).format("YYYY-MM-DD") : "";
    handleFilterChange("timestamps", from && to ? `${from},${to}` : from || to);
  };

  // Handle Apply Filters
  const handleApplyFilters = () => {
    setPagination((prev) => ({ ...prev, page: 0 }));
    fetchTransactions(0, pagination.rowsPerPage);
  };

  // Handle Clear Filters
  const handleClearFilters = () => {
    setFilters({
      name: "",
      phone: "",
      user_id: "",
      client_id: "",
      status: "",
      timestamps: "",
    });
    setFromDate(null);
    setToDate(null);
    setSelectedClient(null);
    setPagination((prev) => ({ ...prev, page: 0 }));
    fetchTransactions(0, pagination.rowsPerPage);
  };

  return (
    <>
      <StatusAlert
        open={alertOpen}
        setOpen={setAlertOpen}
        message={alertMessage}
        severity={alertSeverity}
      />

      {/* Filters Section */}
      <Card sx={{ mb: 2, backgroundColor: "#fff", borderRadius: "8px", boxShadow: "0px 2px 5px rgba(0,0,0,0.1)" }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" color="black" sx={{ mb: 2 }}>
            🔍 Filter Transactions
          </Typography>
          <Grid container spacing={2}>
            {/* Name Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Search Name"
                value={filters.name}
                onChange={(e) => handleFilterChange("name", e.target.value)}
                placeholder="Driver or Client name"
                fullWidth
                size="small"
                helperText="Searches both driver & client names"
                sx={{
                  '& .MuiInputBase-input': { color: 'black' },
                  '& .MuiInputLabel-root': { color: 'black' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'black' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'black' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'black' },
                  '& .MuiFormHelperText-root': { color: 'gray' },
                }}
              />
            </Grid>

            {/* Phone Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Search Phone"
                value={filters.phone}
                onChange={(e) => handleFilterChange("phone", e.target.value)}
                placeholder="Driver or Client phone"
                fullWidth
                size="small"
                helperText="Searches both phones"
                sx={{
                  '& .MuiInputBase-input': { color: 'black' },
                  '& .MuiInputLabel-root': { color: 'black' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'black' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'black' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'black' },
                  '& .MuiFormHelperText-root': { color: 'gray' },
                }}
              />
            </Grid>

            {/* Driver Filter (Text Input) */}
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Driver ID"
                value={filters.user_id}
                onChange={(e) => handleFilterChange("user_id", e.target.value)}
                placeholder="e.g., RPTPLDRV..."
                fullWidth
                size="small"
                helperText="Enter driver ID"
                sx={{
                  '& .MuiInputBase-input': { color: 'black' },
                  '& .MuiInputLabel-root': { color: 'black' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'black' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'black' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'black' },
                  '& .MuiFormHelperText-root': { color: 'gray' },
                }}
              />
            </Grid>

            {/* Client Filter (Autocomplete) */}
            <Grid item xs={12} sm={6} md={2}>
              <Autocomplete
                options={clients}
                getOptionLabel={(option) =>
                  option.company_name || option.name || option.username || "Unknown Client"
                }
                value={selectedClient || (filters.client_id ? { user_id: filters.client_id, company_name: filters.client_id } : null)}
                onChange={(event, newValue) => {
                  setSelectedClient(newValue);
                  handleFilterChange("client_id", newValue ? newValue.user_id : "");
                }}
                onInputChange={(event, newInputValue) => {
                  setClientSearchQuery(newInputValue);
                }}
                filterOptions={(x) => x}
                sx={{
                  width: "100%",
                  backgroundColor: "#fff",
                  '& .MuiInputBase-input': { color: 'black' },
                  '& .MuiInputLabel-root': { color: 'black' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'black' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'black' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'black' },
                  '& .MuiAutocomplete-popupIndicator': { color: 'black' },
                  '& .MuiAutocomplete-clearIndicator': { color: 'black' },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Client"
                    size="small"
                    helperText="By name or company"
                    InputLabelProps={{
                      style: { color: "black" }
                    }}
                    sx={{
                      '& .MuiFormHelperText-root': { color: 'gray' },
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} style={{ color: "black", backgroundColor: "#fff" }}>
                    {option.company_name || option.name || option.username || "Unknown Client"}
                  </li>
                )}
              />
            </Grid>

            {/* Status Filter */}
            <Grid item xs={12} sm={6} md={1}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: 'black' }}>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                  sx={{
                    color: 'black',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'black'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'black'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'black'
                    },
                    '& .MuiSelect-icon': { color: 'black' }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: "white",
                        color: "black"
                      }
                    }
                  }}
                >
                  <MenuItem value="" sx={{ color: "black", backgroundColor: "white", '&:hover': { backgroundColor: "#f5f5f5" } }}>All</MenuItem>
                  <MenuItem value="0" sx={{ color: "black", backgroundColor: "white", '&:hover': { backgroundColor: "#f5f5f5" } }}>Initiated</MenuItem>
                  <MenuItem value="1" sx={{ color: "black", backgroundColor: "white", '&:hover': { backgroundColor: "#f5f5f5" } }}>Processed</MenuItem>
                  <MenuItem value="2" sx={{ color: "black", backgroundColor: "white", '&:hover': { backgroundColor: "#f5f5f5" } }}>Transferred</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Date Range Filter (Calendar) */}
            <Grid item xs={12} sm={6} md={2}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <ThemeProvider theme={createTheme({
                  components: {
                    MuiPickersPopper: {
                      styleOverrides: {
                        root: {
                          '& .MuiPaper-root': {
                            backgroundColor: 'white',
                          },
                        },
                      },
                    },
                  },
                })}>
                  <DatePicker
                    label="From Date"
                    value={fromDate}
                    onChange={handleFromDateChange}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        helperText: "Single date or range",
                        sx: {
                          '& .MuiInputBase-input': { color: 'black' },
                          '& .MuiInputLabel-root': { color: 'black' },
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'black' },
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'black' },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'black' },
                          '& .MuiFormHelperText-root': { color: 'gray' },
                          '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: 'black' }, // Black calendar icon
                        },
                      },
                      popper: {
                        sx: {
                          '&[role="tooltip"]': {
                            backgroundColor: 'white',
                          },
                          '& .MuiPaper-root': {
                            backgroundColor: 'white !important',
                          },
                          '& .MuiPickersCalendarHeader-root': {
                            backgroundColor: 'white !important',
                          },
                          '& .MuiPickersCalendarHeader-label': {
                            color: 'black !important',
                            backgroundColor: 'white !important',
                          },
                          '& .MuiPickersCalendarHeader-switchViewButton': {
                            color: 'black !important',
                          },
                          '& .MuiPickersCalendarHeader-switchViewIcon': {
                            color: 'black !important',
                          },
                          '& .MuiDayCalendar-weekDayLabel': {
                            color: 'black !important',
                          },
                          '& .MuiDayCalendar-header': {
                            color: 'black !important',
                          },
                          '& .MuiPickersDay-root': {
                            color: 'black !important',
                            backgroundColor: 'white !important',
                            '&:hover': {
                              backgroundColor: '#e3f2fd !important',
                              color: 'black !important',
                            },
                            '&.Mui-selected': {
                              backgroundColor: '#2196f3 !important',
                              color: 'white !important',
                            },
                          },
                          '& .MuiPickersArrowSwitcher-button': {
                            color: 'black !important',
                          },
                          '& .MuiPickersArrowSwitcher-icon': {
                            color: 'black !important',
                          },
                          '& .MuiTypography-root': {
                            color: 'black !important',
                          },
                        },
                      },
                    }}
                  />
                </ThemeProvider>
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <ThemeProvider theme={createTheme({
                  components: {
                    MuiPickersPopper: {
                      styleOverrides: {
                        root: {
                          '& .MuiPaper-root': {
                            backgroundColor: 'white',
                          },
                        },
                      },
                    },
                  },
                })}>
                  <DatePicker
                    label="To Date"
                    value={toDate}
                    onChange={handleToDateChange}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        helperText: "Leave empty for single day",
                        sx: {
                          '& .MuiInputBase-input': { color: 'black' },
                          '& .MuiInputLabel-root': { color: 'black' },
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'black' },
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'black' },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'black' },
                          '& .MuiFormHelperText-root': { color: 'gray' },
                          '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: 'black' }, // Black calendar icon
                        },
                      },
                      popper: {
                        sx: {
                          '&[role="tooltip"]': {
                            backgroundColor: 'white',
                          },
                          '& .MuiPaper-root': {
                            backgroundColor: 'white !important',
                          },
                          '& .MuiPickersCalendarHeader-root': {
                            backgroundColor: 'white !important',
                          },
                          '& .MuiPickersCalendarHeader-label': {
                            color: 'black !important',
                            backgroundColor: 'white !important',
                          },
                          '& .MuiPickersCalendarHeader-switchViewButton': {
                            color: 'black !important',
                          },
                          '& .MuiPickersCalendarHeader-switchViewIcon': {
                            color: 'black !important',
                          },
                          '& .MuiDayCalendar-weekDayLabel': {
                            color: 'black !important',
                          },
                          '& .MuiDayCalendar-header': {
                            color: 'black !important',
                          },
                          '& .MuiPickersDay-root': {
                            color: 'black !important',
                            backgroundColor: 'white !important',
                            '&:hover': {
                              backgroundColor: '#e3f2fd !important',
                              color: 'black !important',
                            },
                            '&.Mui-selected': {
                              backgroundColor: '#2196f3 !important',
                              color: 'white !important',
                            },
                          },
                          '& .MuiPickersArrowSwitcher-button': {
                            color: 'black !important',
                          },
                          '& .MuiPickersArrowSwitcher-icon': {
                            color: 'black !important',
                          },
                          '& .MuiTypography-root': {
                            color: 'black !important',
                          },
                        },
                      },
                    }}
                  />
                </ThemeProvider>
              </LocalizationProvider>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12} sm={6} md={1}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleApplyFilters}
                  disabled={loading}
                  sx={{
                    backgroundColor: "#2196f3",
                    "&:hover": { backgroundColor: "#1976d2" },
                  }}
                  startIcon={loading && <CircularProgress size={16} sx={{ color: "white" }} />}
                >
                  {loading ? 'Applying...' : 'Apply'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                  disabled={loading}
                  sx={{
                    borderColor: "black",
                    color: "black",
                  }}
                  startIcon={loading && <CircularProgress size={16} sx={{ color: "black" }} />}
                >
                  {loading ? 'Clearing...' : 'Clear'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Box sx={{
        p: 2,
        width: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column"
      }}>
        {/* Records Info */}
        {pagination.count > 0 && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'bold' }}>
              Total Records: {pagination.count}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              •
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Page {pagination.page + 1} of {pagination.totalPages}
            </Typography>
          </Box>
        )}

        <ThemeProvider theme={getWhiteTheme()}>
          <Box sx={{ position: "relative" }}>
            {loading && transactions.length > 0 && (
              <Box sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                zIndex: 10,
                borderRadius: "4px"
              }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                  <CircularProgress sx={{ color: "#9c27b0" }} />
                  <Typography variant="body2" sx={{ color: "#9c27b0", fontWeight: "bold" }}>
                    Updating...
                  </Typography>
                </Box>
              </Box>
            )}

            {loading && transactions.length === 0 ? (
              <Box sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "300px",
                flexDirection: "column",
                gap: 2
              }}>
                <CircularProgress sx={{ color: "#9c27b0" }} />
                <Typography variant="body2" sx={{ color: "#9c27b0", fontWeight: "bold" }}>
                  Loading transactions...
                </Typography>
              </Box>
            ) : (
              <MUIDataTable
                title={
                  <Typography variant="h5" fontWeight="bold" color="black" sx={{ marginBottom: 0, marginLeft: 0, paddingLeft: 0 }}>
                    Driver Salary Transactions
                  </Typography>
                }
                data={transactions}
                columns={columns}
                options={options}
              />
            )}
          </Box>
        </ThemeProvider>
      </Box>

      {/* Update Dialog */}
      <Dialog
        open={updateDialogOpen}
        onClose={() => setUpdateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "white",
            color: "black"
          }
        }}
      >
        <DialogTitle sx={{ color: "black", backgroundColor: "white", fontWeight: "bold" }}>
          Update Salary Transaction
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "white", color: "black" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            {/* Transaction Info */}
            {selectedTransaction && (
              <Box sx={{
                backgroundColor: "#f5f5f5",
                padding: "12px",
                borderRadius: "4px"
              }}>
                <Typography variant="body2" sx={{ color: "black", fontWeight: "bold", mb: 0.5 }}>
                  Driver: {selectedTransaction.user_username}
                </Typography>
                <Typography variant="body2" sx={{ color: "black", fontWeight: "bold", mb: 0.5 }}>
                  Salary: ₹{parseFloat(selectedTransaction.salary).toFixed(2)}
                </Typography>
                <Typography variant="body2" sx={{ color: "black", fontWeight: "bold" }}>
                  From: {selectedTransaction.from} to {selectedTransaction.till}
                </Typography>
              </Box>
            )}

            {/* Status Dropdown */}
            <FormControl fullWidth>
              <InputLabel sx={{ color: "black", backgroundColor: "white" }}>Status</InputLabel>
              <Select
                value={updateStatus}
                label="Status"
                onChange={(e) => setUpdateStatus(e.target.value)}
                sx={{
                  color: "black",
                  backgroundColor: "white",
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'black'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'black'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'black'
                  },
                  '& .MuiSelect-icon': {
                    color: 'black'
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: "white",
                      color: "black"
                    }
                  }
                }}
              >
                <MenuItem value="0" sx={{ color: "black", backgroundColor: "white", '&:hover': { backgroundColor: "#f5f5f5" } }}>Initiated</MenuItem>
                <MenuItem value="1" sx={{ color: "black", backgroundColor: "white", '&:hover': { backgroundColor: "#f5f5f5" } }}>Processed</MenuItem>
                <MenuItem value="2" sx={{ color: "black", backgroundColor: "white", '&:hover': { backgroundColor: "#f5f5f5" } }}>Transferred</MenuItem>
              </Select>
            </FormControl>

            {/* Transfer ID (Optional) */}
            <TextField
              label="Transfer ID (Optional)"
              value={updateTransferId}
              onChange={(e) => setUpdateTransferId(e.target.value)}
              placeholder="e.g., TXN123456"
              fullWidth
              helperText="Leave empty if not applicable"
              sx={{
                '& .MuiInputBase-input': {
                  color: 'black'
                },
                '& .MuiInputLabel-root': {
                  color: 'black'
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'black'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'black'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'black'
                },
                '& .MuiFormHelperText-root': {
                  color: 'gray'
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: "white", color: "black" }}>
          <Button
            onClick={() => setUpdateDialogOpen(false)}
            sx={{ color: "black" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateSubmit}
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: "#9c27b0",
              "&:hover": { backgroundColor: "#7b1fa2" },
              color: "white"
            }}
          >
            {loading ? <CircularProgress size={24} sx={{ color: "white" }} /> : "Update"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DriverSalaryTransactions;